import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  FileText,
  Calendar,
  MessageSquare,
  AlertCircle,
  Clock,
  Info,
  Check,
  Trash2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { notificationsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useChatWebSocketContext } from '../context/ChatWebSocketContext';
import { useTier } from '../context/TierContext';
import { toast } from 'sonner';
import { formatRelativeNotificationTime } from '../lib/time';

const PAGE_SIZE = 20;
const TYPE_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'quote', label: 'Quote' },
  { value: 'quote_created', label: 'Quote created' },
  { value: 'quote_updated', label: 'Inquiry updated' },
  { value: 'quote_assigned', label: 'Quote assigned' },
  { value: 'bid_received', label: 'Bid received' },
  { value: 'bid_updated', label: 'Bid updated' },
  { value: 'bid_nominated', label: 'Bid nominated' },
  { value: 'quote_rfq_completed', label: 'Shipment completed' },
  { value: 'quote_rfq_accepted', label: 'Shipment completion accepted' },
  { value: 'quote_reminder', label: 'Quote reminder' },
  { value: 'quote_payment_details_requested', label: 'Payment details requested' },
  { value: 'quote_payment_received', label: 'Payment received' },
  { value: 'quote_completed', label: 'Quote completed' },
  { value: 'event', label: 'Event' },
  { value: 'complaint', label: 'Complaint' },
  { value: 'announcement', label: 'Announcement' },
  { value: 'clarification_reply', label: 'Clarification' },
  { value: 'message', label: 'Message' },
  { value: 'alert', label: 'Alert' },
  { value: 'reminder', label: 'Reminder' },
];

function getNotificationIcon(type) {
  const quoteTypes = [
    'quote', 'quote_created', 'quote_updated', 'quote_assigned', 'bid_received', 'bid_updated',
    'bid_nominated', 'quote_rfq_completed', 'quote_rfq_accepted', 'quote_reminder',
    'quote_payment_details_requested', 'quote_payment_received', 'quote_completed',
  ];
  if (quoteTypes.includes(type)) return <FileText size={18} className="text-blue-500 shrink-0" />;
  if (type === 'message') return <MessageSquare size={18} className="text-green-500 shrink-0" />;
  if (type === 'event') return <Calendar size={18} className="text-purple-500 shrink-0" />;
  if (type === 'alert') return <AlertCircle size={18} className="text-red-500 shrink-0" />;
  if (type === 'reminder') return <Clock size={18} className="text-amber-500 shrink-0" />;
  if (type === 'complaint' || type === 'clarification_reply') return <AlertCircle size={18} className="text-orange-500 shrink-0" />;
  if (type === 'announcement') return <Info size={18} className="text-indigo-500 shrink-0" />;
  return <Info size={18} className="text-gray-500 shrink-0" />;
}

function getNotificationLink(referenceType, referenceId) {
  if (!referenceType || !referenceId) return null;
  if (referenceType === 'quote') return { pathname: '/quotes', search: `?quote_id=${referenceId}` };
  if (referenceType === 'event') return { pathname: '/event-connect', search: `?event_id=${referenceId}` };
  if (referenceType === 'complaint') return { pathname: '/complaints', search: `?complaint_id=${referenceId}` };
  if (referenceType === 'announcement') return { pathname: '/dashboard' };
  if (referenceType === 'chat' || referenceType === 'message') return { pathname: '/chat', state: { openConversationId: referenceId } };
  return null;
}

const NotificationsPage = () => {
  const navigate = useNavigate();
  const { member } = useAuth();
  const wsContext = useChatWebSocketContext();
  const { isRouteAccessible } = useTier();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [timeTick, setTimeTick] = useState(Date.now());

  const memberId = member?.member_id;

  const fetchNotifications = useCallback(
    async (isLoadMore = false) => {
      if (!memberId) return;
      const currentSkip = isLoadMore ? skip : 0;
      try {
        if (isLoadMore) setLoadingMore(true);
        else setLoading(true);
        const params = {
          limit: PAGE_SIZE,
          skip: currentSkip,
          unread_only: unreadOnly || undefined,
          type: typeFilter || undefined,
        };
        const res = await notificationsApi.getNotifications(memberId, params);
        const list = res.data || [];
        if (isLoadMore) {
          setNotifications((prev) => [...prev, ...list]);
        } else {
          setNotifications(list);
        }
        setHasMore(list.length === PAGE_SIZE);
        if (!isLoadMore) setSkip(PAGE_SIZE);
        else setSkip((s) => s + PAGE_SIZE);
      } catch (err) {
        console.error('Failed to fetch notifications', err);
        toast.error('Failed to load notifications');
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [memberId, skip, unreadOnly, typeFilter]
  );

  useEffect(() => {
    setSkip(0);
    setHasMore(true);
    fetchNotifications(false);
  }, [memberId, unreadOnly, typeFilter]);

  useEffect(() => {
    const registerNotificationHandler = wsContext?.registerNotificationHandler;
    if (!registerNotificationHandler || !memberId) return;
    const unregister = registerNotificationHandler(() => {
      if (!unreadOnly && !typeFilter) {
        setSkip(0);
        fetchNotifications(false);
      }
    });
    return unregister;
  }, [wsContext?.registerNotificationHandler, memberId, unreadOnly, typeFilter]);

  useEffect(() => {
    const timer = setInterval(() => setTimeTick(Date.now()), 60000);
    return () => clearInterval(timer);
  }, []);

  const handleMarkAllAsRead = async () => {
    if (!memberId) return;
    try {
      await notificationsApi.markAllAsRead(memberId);
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      toast.success('All marked as read');
    } catch (err) {
      toast.error('Failed to mark all as read');
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    if (!memberId) return;
    setSavingId(notificationId);
    try {
      await notificationsApi.markAsRead(notificationId, memberId);
      setNotifications((prev) =>
        prev.map((n) =>
          n.notification_id === notificationId ? { ...n, is_read: true } : n
        )
      );
    } catch (err) {
      toast.error('Failed to mark as read');
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (e, notificationId) => {
    e.stopPropagation();
    if (!memberId) return;
    try {
      await notificationsApi.deleteNotification(notificationId, memberId);
      setNotifications((prev) => prev.filter((n) => n.notification_id !== notificationId));
      toast.success('Notification removed');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleRowClick = (notification) => {
    const link = getNotificationLink(
      notification.reference_type,
      notification.reference_id
    );
    const isNavigable = Boolean(link) && isRouteAccessible(link.pathname);
    if (!isNavigable) {
      return;
    }
    if (!notification.is_read) {
      handleMarkAsRead(notification.notification_id);
    }
    navigate(link);
  };

  const formatTime = (dateString) =>
    formatRelativeNotificationTime(dateString, new Date(timeTick));

  if (!memberId) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
        <p className="text-gray-500 mt-1">View and manage your in-app notifications</p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-wrap items-center gap-4">
            <CardTitle className="text-lg">All notifications</CardTitle>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
            >
              {TYPE_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <label className="flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={unreadOnly}
                onChange={(e) => setUnreadOnly(e.target.checked)}
                className="rounded border-gray-300"
              />
              Unread only
            </label>
            <Button
              variant="outline"
              size="sm"
              onClick={handleMarkAllAsRead}
              className="ml-auto"
            >
              Mark all as read
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading...</div>
          ) : notifications.length === 0 ? (
            <div className="p-12 text-center">
              <Bell size={40} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No notifications</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {notifications.map((n) => {
                const link = getNotificationLink(n.reference_type, n.reference_id);
                const isNavigable = Boolean(link) && isRouteAccessible(link.pathname);

                return (
                  <li
                    key={n.notification_id}
                    className={`flex items-start gap-3 px-4 py-3 ${
                      isNavigable ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default'
                    } ${!n.is_read ? 'bg-blue-50/50' : ''}`}
                    onClick={() => handleRowClick(n)}
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      {getNotificationIcon(n.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm ${
                          !n.is_read ? 'font-medium text-gray-900' : 'text-gray-700'
                        }`}
                      >
                        {n.title}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                        {n.message}
                      </p>
                      {n.action_pending && (
                        <p className="text-xs text-blue-600 mt-1 font-medium">
                          Action: {n.action_pending}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        {formatTime(n.created_at)}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {!n.is_read && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkAsRead(n.notification_id);
                          }}
                          disabled={savingId === n.notification_id}
                          title="Mark as read"
                        >
                          <Check size={16} />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-gray-400 hover:text-red-600"
                        onClick={(e) => handleDelete(e, n.notification_id)}
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          {hasMore && !loading && notifications.length > 0 && (
            <div className="p-3 border-t text-center">
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchNotifications(true)}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading...' : 'Load more'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationsPage;
